/**
 * Consent Mode v2 state management for Google Analytics.
 *
 * Two independent consent channels are tracked so users can accept analytics
 * without accepting ads, or vice versa, in line with Consent Mode v2's own
 * signal separation:
 *
 *   - `ad_storage` gates ads-adjacent gtag signals
 *       (ad_storage / ad_user_data / ad_personalization)
 *     AND server-side Reddit CAPI + Google Ads conversion events.
 *
 *   - `analytics_storage` gates gtag's analytics_storage signal AND
 *     server-side GA4 Measurement Protocol events (sign_up / purchase).
 *
 * Defaults are "denied" globally until a decision exists. Non-consent-required
 * countries (for example US) are silent-granted in the bootstrap when the
 * request carries a trusted edge country code, so GA4 can write ``_ga`` before
 * first-visit signup. Unknown country and consent-required regions stay denied
 * until the banner or the client geo lookup decides. The choice is persisted
 * in a first-party cookie so the banner does not re-prompt on every visit.
 *
 * The EU/EEA region list is passed alongside the global default as documented
 * in the gtag consent reference: gtag updates apply globally, but
 * region-scoped defaults take precedence when they overlap for users in
 * those regions.
 */

import type { ConsentSignals, ConsentState } from "./types";

export const CONSENT_COOKIE_NAME = "nrs_ga_consent";
/**
 * Cookie-payload version. Bumped from `"1"` (single `choice` field) to
 * `"2"` when dual-channel consent (ad_storage + analytics_storage)
 * landed. Legacy `"1"` payloads are still read: the single `choice`
 * value is applied to both channels to avoid re-prompting an already
 * consented population. New writes always use v2.
 */
export const CONSENT_COOKIE_VERSION = "2";
const CONSENT_COOKIE_MAX_AGE_SEC = 180 * 24 * 60 * 60;

/**
 * ISO 3166-1 alpha-2 codes of jurisdictions where we must obtain prior,
 * explicit consent before loading Google Analytics or dropping analytics
 * cookies. This list is used for both:
 *
 *   1. Consent Mode v2 region-scoped defaults (gtag consent 'default'), and
 *   2. UI banner gating (users in other regions do not see the banner).
 *
 * Coverage rationale:
 * - EU + EEA (GDPR)
 * - United Kingdom (UK GDPR + PECR)
 * - Switzerland (FADP 2023)
 * - Brazil (LGPD)
 *
 * Users outside these regions do not see the banner but still retain a
 * persistent "Cookie preferences" opt-out in the site footer so they can
 * revoke consent at any time (CCPA/CPRA-compatible posture).
 */
export const CONSENT_REQUIRED_REGIONS: readonly string[] = [
  // EU member states
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // Non-EU EEA
  "IS",
  "LI",
  "NO",
  // United Kingdom
  "GB",
  // Switzerland (FADP)
  "CH",
  // Brazil (LGPD)
  "BR",
];

export type ConsentRequiredRegion = (typeof CONSENT_REQUIRED_REGIONS)[number];

/**
 * Backwards-compatible alias. New code should import
 * `CONSENT_REQUIRED_REGIONS` directly.
 *
 * @deprecated Use CONSENT_REQUIRED_REGIONS.
 */
export const EU_CONSENT_REGIONS = CONSENT_REQUIRED_REGIONS;

/**
 * Edge-geo sentinels that are not ISO countries. Cloudflare uses XX
 * (unknown) and T1 (Tor); App Engine uses ZZ. Treat as unknown: fail closed.
 */
export const UNKNOWN_COUNTRY_SENTINELS: readonly string[] = ["XX", "T1", "ZZ"];

/**
 * One of the two values an individual consent channel can hold.
 * Same shape as Consent Mode v2's gtag signal state.
 */
export type StoredConsentChoice = "granted" | "denied";

/**
 * Granular per-channel consent record persisted on the client.
 *
 * `choice` is retained as a derived summary (= "granted" iff BOTH channels
 * are granted) so existing callers that treat the record as a single opt-in
 * flag (Reddit CAPI consent relay, userManagement.createUser) keep working
 * without branching. New code should consume `ad_storage` /
 * `analytics_storage` directly.
 */
export interface StoredConsentRecord {
  version: string;
  /** Ads / Reddit CAPI / Google Ads gate. */
  ad_storage: StoredConsentChoice;
  /** GA4 client-side + server-side Measurement Protocol gate. */
  analytics_storage: StoredConsentChoice;
  /**
   * Derived summary: "granted" iff BOTH channels are granted. Kept for
   * back-compatibility with call sites that predate the split.
   */
  choice: StoredConsentChoice;
  timestampMs: number;
  /**
   * True when the user explicitly granted ad_storage while the
   * `navigator.globalPrivacyControl` (GPC) signal was active. Per
   * CCPA/CPRA §7025(c)(6), a business may honor an explicit opt-in
   * *after* receiving a GPC signal — but only if the opt-in was given
   * in the presence of the signal. We record this so a later GPC toggle
   * does not silently re-grant consent that was made under different
   * circumstances. Scoped to ad_storage because GPC's legal basis is
   * advertising opt-out; analytics_storage consent is not derived from GPC.
   */
  gpcOverride?: boolean;
}

/**
 * Returns true when the browser/extension has set the Global Privacy Control
 * (GPC) signal. Required by California AG enforcement (Sephora, 2022) and
 * Colorado CPA rules: a GPC signal MUST be honored as an opt-out of sale /
 * sharing / targeted advertising without further user interaction.
 */
export function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  const withGpc = navigator as Navigator & { globalPrivacyControl?: boolean };
  return withGpc.globalPrivacyControl === true;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSec: number): void {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:";
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSec}`,
    "Path=/",
    "SameSite=Lax",
  ];
  if (secure) {
    parts.push("Secure");
  }
  document.cookie = parts.join("; ");
}

function deriveSummary(
  ad: StoredConsentChoice,
  analytics: StoredConsentChoice
): StoredConsentChoice {
  return ad === "granted" && analytics === "granted" ? "granted" : "denied";
}

/**
 * Read the persisted consent record.
 *
 * Handles both the current v2 payload (explicit `ad_storage` /
 * `analytics_storage`) and the legacy v1 payload (single `choice` field)
 * by upgrading v1 to v2 in memory: the pre-split user population consented
 * to both ad_storage and analytics_storage together, so we preserve that
 * decision until they revisit the banner.
 */
/**
 * Parse a stored consent cookie payload. Used by the browser reader and by
 * server routes that receive `nrs_ga_consent` on the request.
 */
export function parseStoredConsentPayload(
  raw: string | null | undefined
): StoredConsentRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredConsentRecord> & {
      choice?: StoredConsentChoice;
    };
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.timestampMs !== "number") return null;
    if (parsed.gpcOverride !== undefined && typeof parsed.gpcOverride !== "boolean") {
      return null;
    }

    if (parsed.version === CONSENT_COOKIE_VERSION) {
      if (
        (parsed.ad_storage !== "granted" && parsed.ad_storage !== "denied") ||
        (parsed.analytics_storage !== "granted" &&
          parsed.analytics_storage !== "denied")
      ) {
        return null;
      }
      return {
        version: CONSENT_COOKIE_VERSION,
        ad_storage: parsed.ad_storage,
        analytics_storage: parsed.analytics_storage,
        choice: deriveSummary(parsed.ad_storage, parsed.analytics_storage),
        timestampMs: parsed.timestampMs,
        ...(parsed.gpcOverride ? { gpcOverride: true } : {}),
      };
    }

    // Legacy v1: upgrade in-memory without rewriting the cookie to avoid a
    // silent mutation of the user's stored state. Next banner interaction
    // (Accept / Decline / Customize) will persist a fresh v2 record.
    if (parsed.version === "1") {
      const legacyChoice = parsed.choice;
      if (legacyChoice !== "granted" && legacyChoice !== "denied") return null;
      return {
        version: CONSENT_COOKIE_VERSION,
        ad_storage: legacyChoice,
        analytics_storage: legacyChoice,
        choice: legacyChoice,
        timestampMs: parsed.timestampMs,
        ...(parsed.gpcOverride ? { gpcOverride: true } : {}),
      };
    }

    return null;
  } catch {
    return null;
  }
}

export function readStoredConsent(): StoredConsentRecord | null {
  return parseStoredConsentPayload(readCookie(CONSENT_COOKIE_NAME));
}

/**
 * Persist a dual-channel consent decision.
 *
 * The derived `choice` summary is recorded alongside the granular channels
 * so back-compat readers (Reddit CAPI relay, createUser) can continue to
 * treat "granted" as the "opt-in to everything" shorthand without parsing
 * every field.
 */
export function persistConsentChoice(
  signals: {
    ad_storage: StoredConsentChoice;
    analytics_storage: StoredConsentChoice;
  },
  options?: { gpcOverride?: boolean }
): void {
  const record: StoredConsentRecord = {
    version: CONSENT_COOKIE_VERSION,
    ad_storage: signals.ad_storage,
    analytics_storage: signals.analytics_storage,
    choice: deriveSummary(signals.ad_storage, signals.analytics_storage),
    timestampMs: Date.now(),
    ...(options?.gpcOverride ? { gpcOverride: true } : {}),
  };
  writeCookie(CONSENT_COOKIE_NAME, JSON.stringify(record), CONSENT_COOKIE_MAX_AGE_SEC);
}

/**
 * Convert granular choices into a full gtag Consent Mode v2 signals object.
 *
 * `ad_storage` controls the three ads signals (ad_storage / ad_user_data /
 * ad_personalization); `analytics_storage` controls its own signal only.
 * This mapping is the canonical place where the split between the two
 * banner toggles and the four gtag signals is resolved.
 */
export function choicesToSignals(
  ad: StoredConsentChoice,
  analytics: StoredConsentChoice
): ConsentSignals {
  const adState: ConsentState = ad === "granted" ? "granted" : "denied";
  const analyticsState: ConsentState =
    analytics === "granted" ? "granted" : "denied";
  return {
    ad_storage: adState,
    ad_user_data: adState,
    ad_personalization: adState,
    analytics_storage: analyticsState,
  };
}

/**
 * Legacy single-channel helper kept for call sites that pre-date the
 * ad_storage / analytics_storage split. Produces the same gtag signals
 * object as if both channels had the given value. New code should pass
 * the two channels explicitly via `choicesToSignals`.
 *
 * @deprecated Use choicesToSignals(ad, analytics).
 */
export function choiceToSignals(choice: StoredConsentChoice): ConsentSignals {
  return choicesToSignals(choice, choice);
}

export interface ConsentBootstrapOptions {
  /**
   * ISO 3166-1 alpha-2 from a trusted edge header. When set and the country
   * is not consent-required, the bootstrap silent-grants and persists the
   * first-party cookie before gtag.js loads. Unknown / missing stays denied.
   */
  edgeCountryCode?: string | null;
}

/**
 * True when this country may be silent-granted (no banner) for analytics.
 * Unknown / empty is fail-closed.
 */
export function canSilentGrantForCountry(
  countryCode: string | null | undefined
): boolean {
  if (!countryCode) return false;
  const upper = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(upper)) return false;
  if ((UNKNOWN_COUNTRY_SENTINELS as readonly string[]).includes(upper)) {
    return false;
  }
  return !(CONSENT_REQUIRED_REGIONS as readonly string[]).includes(upper);
}

/**
 * Inline bootstrap script executed before the gtag library loads.
 *
 * - Initializes dataLayer + gtag shim so queued calls survive the load race.
 * - Sets Consent Mode v2 defaults to "denied" globally (with explicit EU list)
 *   so we are compliant for any jurisdiction until the user opts in.
 * - Re-applies any persisted consent choice immediately via a consent update,
 *   honoring the granular v2 payload when present and falling back to the v1
 *   `choice` field for users with still-live pre-split cookies.
 * - When the edge country is known and not consent-required, silent-grants
 *   both channels and writes the consent cookie so first-visit signup can
 *   read ``analytics_storage=granted`` and resolve ``_ga``.
 * - When the edge country is unknown, starts the Cloudflare trace immediately
 *   (before React) so a US visitor is granted before they can finish signup.
 *   GPC skips silent grant; ConsentBanner persists the deny.
 */
export function buildConsentBootstrapScript(
  options?: ConsentBootstrapOptions
): string {
  const euRegionsJson = JSON.stringify(CONSENT_REQUIRED_REGIONS);
  const silentGrant = canSilentGrantForCountry(options?.edgeCountryCode);
  const defaultSignals: ConsentSignals = {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  };
  return `
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);} 
    window.gtag = gtag;

    gtag('consent', 'default', ${JSON.stringify({
      ...defaultSignals,
      region: "__EU_REGIONS__",
      wait_for_update: 500,
    })
      .replace('"__EU_REGIONS__"', euRegionsJson)});

    gtag('consent', 'default', ${JSON.stringify({
      ...defaultSignals,
      wait_for_update: 500,
    })});

    var nrsConsentRequiredRegions = ${euRegionsJson};
    var nrsUnknownCountrySentinels = ${JSON.stringify(UNKNOWN_COUNTRY_SENTINELS)};
    var nrsEdgeSilentGrant = ${silentGrant ? "true" : "false"};

    function nrsHasGpc() {
      return (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true);
    }

    function nrsPersistSilentGrant() {
      if (nrsHasGpc()) return false;
      gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
      var record = {
        version: '${CONSENT_COOKIE_VERSION}',
        ad_storage: 'granted',
        analytics_storage: 'granted',
        choice: 'granted',
        timestampMs: Date.now(),
      };
      var secure = (typeof window !== 'undefined' && window.location.protocol === 'https:');
      var parts = [
        '${CONSENT_COOKIE_NAME}=' + encodeURIComponent(JSON.stringify(record)),
        'Max-Age=${CONSENT_COOKIE_MAX_AGE_SEC}',
        'Path=/',
        'SameSite=Lax',
      ];
      if (secure) parts.push('Secure');
      document.cookie = parts.join('; ');
      return true;
    }

    function nrsReadConsentCookieRaw() {
      var raw = document.cookie.match(/(?:^|; )${CONSENT_COOKIE_NAME}=([^;]*)/);
      return raw ? raw[1] : null;
    }

    try {
      var raw = nrsReadConsentCookieRaw();
      if (raw) {
        var parsed = JSON.parse(decodeURIComponent(raw));
        if (parsed && (parsed.version === '${CONSENT_COOKIE_VERSION}' || parsed.version === '1')) {
          // Normalize both cookie generations into the four gtag signals.
          // v2 has explicit ad_storage / analytics_storage fields; v1 had
          // a single top-level choice value that applied to both signals
          // at once.
          var adChoice = parsed.version === '${CONSENT_COOKIE_VERSION}'
            ? parsed.ad_storage
            : parsed.choice;
          var analyticsChoice = parsed.version === '${CONSENT_COOKIE_VERSION}'
            ? parsed.analytics_storage
            : parsed.choice;
          var validAd = adChoice === 'granted' || adChoice === 'denied';
          var validAnalytics =
            analyticsChoice === 'granted' || analyticsChoice === 'denied';
          if (validAd && validAnalytics) {
            // Honor Global Privacy Control (CCPA/CPRA §7025(c)(6)): if the
            // browser is currently signaling GPC, we ignore any stored
            // "granted" ad_storage choice UNLESS the user explicitly opted
            // in while GPC was already active. Analytics_storage is not
            // derived from GPC so it passes through unchanged.
            var gpc = nrsHasGpc();
            var effectiveAd = adChoice;
            if (effectiveAd === 'granted' && gpc && parsed.gpcOverride !== true) {
              effectiveAd = 'denied';
            }
            gtag('consent', 'update', {
              ad_storage: effectiveAd,
              ad_user_data: effectiveAd,
              ad_personalization: effectiveAd,
              analytics_storage: analyticsChoice,
            });
          }
        }
      } else if (nrsEdgeSilentGrant) {
        nrsPersistSilentGrant();
      } else if (typeof fetch === 'function') {
        fetch('https://www.cloudflare.com/cdn-cgi/trace', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit',
        }).then(function (response) {
          if (!response || !response.ok) return null;
          return response.text();
        }).then(function (body) {
          if (!body || nrsReadConsentCookieRaw()) return;
          var loc = null;
          var lines = body.split('\\n');
          for (var i = 0; i < lines.length; i++) {
            var eq = lines[i].indexOf('=');
            if (eq <= 0) continue;
            if (lines[i].slice(0, eq).trim().toLowerCase() !== 'loc') continue;
            var value = lines[i].slice(eq + 1).trim().toUpperCase();
            if (/^[A-Z]{2}$/.test(value)) loc = value;
            break;
          }
          if (
            !loc ||
            nrsUnknownCountrySentinels.indexOf(loc) !== -1 ||
            nrsConsentRequiredRegions.indexOf(loc) !== -1
          ) return;
          nrsPersistSilentGrant();
        }).catch(function () { /* geo miss stays denied until ConsentBanner */ });
      }
    } catch (_e) { /* ignored: consent cookie is best-effort restore */ }
  `;
}
